
import { useDroppable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { useSortable } from "@dnd-kit/sortable";

import { FieldTypeName, renderers } from "./fields";
import { cn } from "@/lib/utils";
import { FieldType } from "./context";
import { GripVerticalIcon } from "lucide-react";
import { useFormContext } from "./context";
import { FormEditorMode } from "./types";

function getRenderer(field: FieldType) {
  if (field.type === "spacer") {
    // Spacer-specific rendering logic, a simple placeholder or visual representation
    return renderers[field.name as FieldTypeName] || (() => (
      <div className="w-full h-10 border-2 rounded-md border-primary min-w-64 bg-primary/10"></div>
    ));
  }

  // Return the appropriate renderer based on field type
  return renderers[field.type] || (() => (
    <div>No renderer found for {field.type}</div>
  ));
}

type FieldProps = {
  field: FieldType;
  overlay?: boolean;
  preview?: boolean;
  disabled?: boolean;
}
export function Field(props:FieldProps) {
  const { field, overlay, preview, disabled, ...rest } = props;

  const Component = getRenderer(field);

  return (
    <div className={cn(
       "bg-input/50",
      overlay && "border rounded-md flex"
    )}>
      {(overlay && !preview) &&
      <div className="flex items-center px-2 border-r hover:bg-muted cursor-grabbing">
        <GripVerticalIcon className="w-4 h-4 text-neutral-500"/>
      </div>
      }
      
      <div className="w-full p-3">
        <Component field={field} preview={preview} disabled={disabled} {...rest} />
      </div>
      
    </div>
  );
}

type SortableFieldProps = {
  id: string;
  index: number;
  field: FieldType;
  mode: FormEditorMode;
}

function SortableField(props:SortableFieldProps) {
  const { id, index, field, mode } = props;

  const { listeners, setNodeRef, transform, transition } =
    useSortable({
      id,
      data: {
        index,
        id,
        field,
      },
    });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: field.type === "spacer" ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex w-full max-w-xl my-2 bg-background border rounded-md shadow-sm">
      {mode !== "preview" &&
      <div  {...listeners} className="flex items-center px-2 border-r hover:bg-input/50 cursor-grab">
        <GripVerticalIcon className="w-4 h-4 text-neutral-500"/>
      </div>
      }
      
      <div className="w-full">
        <Field field={field} preview={mode === "preview"}/>
      </div>
    </div>
  );
}

type CanvasProps = {
  fields: FieldType[]
  mode: FormEditorMode
}

export default function Canvas({fields, mode}:CanvasProps) {
  const { form_name, setform_name, form_description, setform_description } = useFormContext()

  const { setNodeRef } = useDroppable({
    id: "canvas_droppable",
    data: {
      parent: null,
      isContainer: true,
    },
  });

  return (
    <div ref={setNodeRef} className="w-full p-4 ml-4 overflow-auto rounded-md shadow-inner bg-background bg-dot-4-s-1-neutral-200 border" 
    >
      {mode === "preview"
      ?
      <div className="flex flex-col items-center justify-center w-full gap-4 my-4">
        <p className="w-full max-w-xl text-xl font-semibold text-left bg-transparent md:text-4xl focus:outline-none placeholder-neutral-400">{form_name}</p>
        <p className="w-full max-w-xl text-left bg-transparent focus:outline-none placeholder-neutral-400">{form_description}</p>
      </div>
      :
      <div className="flex flex-col items-center justify-center w-full gap-4 my-4">
        <input value={form_name} onChange={(e)=>setform_name(e.currentTarget.value)} className="w-full max-w-xl text-xl font-semibold text-left bg-transparent md:text-4xl focus:outline-none placeholder-neutral-400" type="text" placeholder="Form titel"/>
        <input value={form_description} onChange={(e)=>setform_description(e.currentTarget.value)} className="w-full max-w-xl text-left bg-transparent focus:outline-none placeholder-neutral-400" type="text" placeholder="Omschrijving (optioneel)"/>
      </div>
      }
      
      
      <div className="flex flex-col items-center">
        {fields?.map((f, i) => (
          <SortableField key={f.id} id={f.id} field={f} index={i} mode={mode}/>
        ))}
      </div>
    </div>
  );
}
